'use client';

import { useState, useMemo } from 'react';
import { Button, Chip, Input } from '@heroui/react';
import { FaCheckCircle, FaTimes } from 'react-icons/fa';
import { DiscountCode } from '@/util/types/serverTypeCollection/discounts';
import {
  validateDiscountCode,
  getEligibleServiceIds,
} from '@/util/functions/discountCodeValidator';
import { formatDiscountLabel } from '@/util/functions/formatDiscountLabel';
import { useScheduler } from '@/app/scheduler/SchedulerContext';

interface DiscountCodeInputProps {
  discountCodes: DiscountCode[];
  appliedDiscountCode: DiscountCode | null;
  appliedDiscountServiceIds: string[];
  selectedServiceIds: string[];
  onApply: (code: DiscountCode, serviceIds: string[]) => void;
  onClear: () => void;
}

export default function DiscountCodeInput({
  discountCodes,
  appliedDiscountCode,
  appliedDiscountServiceIds,
  selectedServiceIds,
  onApply,
  onClear,
}: DiscountCodeInputProps) {
  const { companyStyles } = useScheduler();
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  // Handle form submission
  const handleApply = async () => {
    if (!inputValue.trim()) {
      setValidationError('Please enter a discount code');
      return;
    }

    setIsValidating(true);
    setValidationError(null);

    // Find the code in the provided list (case-insensitive)
    const codeToFind = inputValue.trim().toUpperCase();
    const foundCode = discountCodes.find(
      (c) => c.code.toUpperCase() === codeToFind
    );

    if (!foundCode) {
      setValidationError('Invalid discount code');
      setIsValidating(false);
      return;
    }

    // Validate the code
    const validation = validateDiscountCode(
      foundCode.code,
      [foundCode],
      selectedServiceIds
    );

    if (!validation.isValid) {
      setValidationError(validation.error || 'Invalid discount code');
      setIsValidating(false);
      return;
    }

    // Get eligible services
    const eligibleServiceIds = getEligibleServiceIds(
      foundCode,
      selectedServiceIds
    );

    if (eligibleServiceIds.length === 0) {
      setValidationError(
        'This discount code is not applicable to your selected services'
      );
      setIsValidating(false);
      return;
    }

    // Apply the discount code
    if (foundCode.appliesToAllServices) {
      onApply(foundCode, selectedServiceIds);
    } else {
      onApply(foundCode, eligibleServiceIds);
    }

    setInputValue('');
    setIsValidating(false);
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isValidating) {
      handleApply();
    }
  };

  // Get discount display text
  const discountText = useMemo(
    () => (appliedDiscountCode ? formatDiscountLabel(appliedDiscountCode) : null),
    [appliedDiscountCode]
  );

  // Show applied discount code
  if (appliedDiscountCode) {
    return (
      <div className='space-y-2'>
        <div className='flex items-center gap-2'>
          <Chip
            color='success'
            variant='flat'
            startContent={<FaCheckCircle size={14} />}
            classNames={{
              base: 'border-success-200 bg-success-50',
              content: 'text-success-700 font-semibold',
            }}
          >
            {appliedDiscountCode.code}
          </Chip>
          {discountText && (
            <span className='text-sm font-medium text-success-700'>
              {discountText}
            </span>
          )}
          <Button
            isIconOnly
            size='sm'
            variant='light'
            color='danger'
            onPress={onClear}
            className='ml-auto'
            aria-label='Remove discount code'
          >
            <FaTimes />
          </Button>
        </div>
        {appliedDiscountCode.name && (
          <p className='text-xs text-gray-600'>{appliedDiscountCode.name}</p>
        )}
        {!appliedDiscountCode.appliesToAllServices &&
          appliedDiscountServiceIds.length > 0 && (
            <p className='text-xs text-gray-500'>
              Applied to {appliedDiscountServiceIds.length} service
              {appliedDiscountServiceIds.length !== 1 ? 's' : ''}
            </p>
          )}
      </div>
    );
  }

  // Show input form field
  return (
    <div className='space-y-2'>
      <div className='flex gap-2'>
        <Input
          label='Discount Code'
          placeholder='Enter code'
          value={inputValue}
          onValueChange={(value) => {
            setInputValue(value);
            setValidationError(null);
          }}
          onKeyDown={handleKeyPress}
          isInvalid={!!validationError}
          errorMessage={validationError || undefined}
          classNames={{
            inputWrapper: 'border bg-white shadow-none',
            label: 'font-light uppercase text-xs',
            // input: 'text-base md:text-sm',
          }}
          variant='bordered'
          isDisabled={isValidating}
          endContent={
            <Button
              onPress={handleApply}
              isDisabled={!inputValue.trim() || isValidating}
              className={`font-bold uppercase ${companyStyles.secondaryButtonClassName}`}
              style={companyStyles.secondaryButtonStyles}
            >
              Apply
            </Button>
          }
        />
      </div>
    </div>
  );
}
